import { useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { LiveStatusFilter } from '@manaro/contracts'
import { ChatScreen, FullScreenLayer } from '@/features/support-chat/components/chat-screen.tsx'
import { ConversationListItem } from '@/features/support-chat/components/conversation-list-item.tsx'
import { useLiveConversation, useLiveConversations, useLiveSummary } from '@/features/support-chat/use-support-chat.ts'
import { ApiError } from '@/shared/api/client.ts'
import { useWideScreen } from '@/shared/hooks/use-media-query.ts'
import { Card } from '@/shared/ui/card.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { Alert, EmptyState, ErrorState, Skeleton } from '@/shared/ui/states.tsx'

const FILTERS: Array<{ value: LiveStatusFilter; label: string }> = [
  { value: 'open', label: 'Открытые' },
  { value: 'waiting', label: 'Ждут оператора' },
  { value: 'operator', label: 'У оператора' },
  { value: 'closed', label: 'Закрытые' },
]

function BackToList({ onBack }: { onBack?: () => void }) {
  if (!onBack) return null
  return (
    <button
      type="button"
      onClick={onBack}
      className="flex min-h-[44px] items-center px-4 pt-[env(safe-area-inset-top)] text-left text-[15px] text-brand"
    >
      ‹ К чатам
    </button>
  )
}

function ConversationPane({ id, onBack }: { id: string; onBack?: () => void }) {
  const detail = useLiveConversation(id)

  // Once something is on screen a failed poll must not tear down the chat and the draft in the composer.
  if (detail.data) return <ChatScreen detail={detail.data} offline={detail.isError} onBack={onBack} />

  if (detail.isPending) {
    return (
      <div className="flex flex-col">
        <BackToList onBack={onBack} />
        <div className="flex flex-col gap-3 p-5">
          <Skeleton className="h-14" />
          <Skeleton className="h-24" />
          <Skeleton className="h-16" />
        </div>
      </div>
    )
  }
  return (
    <div className="flex h-full flex-col">
      <BackToList onBack={onBack} />
      <ErrorState
        message={detail.error instanceof ApiError ? detail.error.message : 'Не удалось открыть разговор.'}
        onRetry={() => void detail.refetch()}
      />
    </div>
  )
}

export function SupportLivePage() {
  const { id = null } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const wide = useWideScreen()
  const [filter, setFilter] = useState<LiveStatusFilter>('open')

  const summary = useLiveSummary()
  const list = useLiveConversations(filter)
  const conversations = list.data?.conversations ?? []
  // Opened from the list → «back» returns along history, so the phone's own back button agrees with ours.
  // Opened from a Telegram link → «back» lands on the list instead of leaving the app.
  const open = (conversationId: string) =>
    wide
      ? navigate(`/support/live/${conversationId}`, { replace: Boolean(id) })
      : navigate(`/support/live/${conversationId}`, { state: { fromList: true } })
  const backToList = () =>
    (location.state as { fromList?: boolean } | null)?.fromList
      ? navigate(-1)
      : navigate('/support/live', { replace: true })

  const counts: Partial<Record<LiveStatusFilter, number>> = summary.data
    ? { open: summary.data.open, waiting: summary.data.waiting, operator: summary.data.withOperator }
    : {}

  if (list.data && !list.data.available) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeading />
        <Alert>
          Чат не подключён: в настройках сервера не задан адрес базы (<code>SUPPORT_CHAT_DATABASE_URL</code>).
        </Alert>
      </div>
    )
  }

  const listCard = (
    <Card className="-mx-4 flex min-h-0 flex-col overflow-hidden rounded-none border-x-0 lg:mx-0 lg:h-[calc(100vh-210px)] lg:min-h-[480px] lg:rounded-card lg:border-x">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : list.error ? (
          <ErrorState
            message={list.error instanceof ApiError ? list.error.message : 'Не удалось загрузить разговоры.'}
            onRetry={() => void list.refetch()}
          />
        ) : conversations.length === 0 ? (
          <EmptyState
            title="Здесь пусто"
            description="Как только кто-то напишет в чат на сайте или в CRM, разговор появится здесь."
          />
        ) : (
          conversations.map((item) => (
            <ConversationListItem key={item.id} item={item} active={item.id === id} onSelect={() => open(item.id)} />
          ))
        )}
      </div>
    </Card>
  )

  return (
    <div className="flex flex-col gap-4 lg:gap-5">
      <PageHeading />

      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] lg:mx-0 lg:flex-wrap lg:px-0">
        {FILTERS.map((item) => (
          <span key={item.value} className="shrink-0">
            <Chip
              label={item.label}
              count={counts[item.value]}
              active={filter === item.value}
              tone={item.value === 'waiting' ? 'tint' : 'filled'}
              onClick={() => setFilter(item.value)}
            />
          </span>
        ))}
      </div>

      {wide ? (
        <div className="grid grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-5">
          {listCard}
          <Card className="h-[calc(100vh-210px)] min-h-[480px] overflow-hidden">
            {id ? (
              <ConversationPane key={id} id={id} />
            ) : (
              <EmptyState
                title="Выберите разговор"
                description="Бот отвечает сам. Когда он не справляется или посетитель зовёт человека, разговор поднимается наверх со статусом «Ждёт оператора»."
              />
            )}
          </Card>
        </div>
      ) : (
        <>
          {listCard}
          {/* Phones: the conversation opens over the list, which keeps its scroll for the way back. */}
          {id && (
            <FullScreenLayer>
              <ConversationPane key={id} id={id} onBack={backToList} />
            </FullScreenLayer>
          )}
        </>
      )}
    </div>
  )
}

function PageHeading() {
  return (
    <header className="flex flex-col gap-1">
      <h1 className="font-display text-[28px] leading-tight font-semibold tracking-[-0.02em] lg:text-[22px] lg:font-medium lg:tracking-[-0.015em]">
        <span className="lg:hidden">Чат</span>
        <span className="hidden lg:inline">Чат на сайте</span>
      </h1>
      <p className="hidden text-[14px] text-ink-muted lg:block">
        Живые разговоры из окна поддержки в CRM и на сайте. Можно подключиться вместо бота и вернуть разговор ему.
      </p>
    </header>
  )
}
